import React, { MouseEvent, useCallback, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { BaseLogo, LogoType } from "../../../core/models/entities/BaseLogo";
import { Role } from "../../../core/models/entities/Role";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useOrganizationImpersonationContext } from "../../../contexts/OrganizationImpersonationContext";
import { enviroment } from "../../../enviroment";
import {
  isLogoDetailFromList,
  isLogoDetailQueryKey,
  logoDetailKey,
} from "../../../data/repositories/logoDetailCache";
import {
  PriceRefreshNowCacheWrite,
  useLogoPriceRefreshNow,
} from "../../../hooks/useLogoPriceRefreshNow";
import {
  isLogoProductPending,
  isProductPendingStale,
} from "../../../utils/functions/productPending";

// Lifecycle of the popup's background GetLogo (LogoDetailStatus in
// logo-details.component) - a local alias so this leaf component does not
// import from a screen.
type DetailStatus = "loading" | "ready" | "error";

/** Where the button sits: the logo popup (default) or the logo editor's toolbar. */
export type PriceRefreshNowSurface = "popup" | "editor";

interface Props {
  logo: BaseLogo;
  detailStatus?: DetailStatus;
  className?: string;
  // "editor" (flag REACT_APP_LOGO_PRICE_REFRESH_NOW_EDITOR_ENABLED on top of
  // the popup flag): the lock is judged on the freshest cached detail and a
  // click marks only the list rows - see EditorPriceRefreshNowButton.
  surface?: PriceRefreshNowSurface;
}

// The state the popup's Inner needs beyond the props; the editor wrapper
// fills them, the popup never does (undefined = exactly the popup's path).
interface InnerProps extends Props {
  pendingLogo?: BaseLogo;
  cacheWrite?: PriceRefreshNowCacheWrite;
}

// The small "refresh prices now" icon button in the logo popup's price
// column (flag REACT_APP_LOGO_PRICE_REFRESH_NOW_ENABLED): admin only, image
// logos only (the API's worker handles nothing else and answers 409 for the
// rest). One click = ONE POST product-refresh (useLogoPriceRefreshNow), then
// locked for the API's cooldown with a native tooltip saying when it opens
// again (or "daily limit reached" when the API's global cap is the lock);
// locked as well while the logo is FRESHLY "Prices on their way"
// (isLogoProductPending with a stamp younger than the 10-minute stale line -
// the API's 409 alreadyPending would refuse it anyway) and while the popup's
// background GetLogo has not landed (detailStatus "loading" or "error") -
// the cache patch after the answer must hit a real detail entry, never the
// list stub, so the pending badge appears and the pending poll is armed. A
// STALE pending logo (badge "Prices delayed": the worker poisoned or the
// sweep is on it) is the one case the admin must be able to re-drive, so
// the button stays open there and the API answers 202 with a new stamp.
// Flag off = nothing rendered (no hook, no subscription): the DOM stays
// byte-identical. surface "editor" needs its own flag on top (both
// "true"); with it off the popup's path is untouched and the editor
// renders nothing.
export function PriceRefreshNowButton(props: Props) {
  if (!enviroment.logoPriceRefreshNowEnabled) return null;
  if (props.surface === "editor") {
    if (!enviroment.logoPriceRefreshNowEditorEnabled) return null;
    return <EditorPriceRefreshNowButton {...props} />;
  }
  return <PriceRefreshNowButtonInner {...props} />;
}

// The editor's toolbar (flag REACT_APP_LOGO_PRICE_REFRESH_NOW_EDITOR_ENABLED
// on top of the popup flag; LogoEditorTool - the standalone editor and the
// design editor's logo tool). Two things differ from the popup:
//  - the "already pending" lock is judged on the FRESHEST state known: the
//    cached detail entry when it is a real GetLogo (the editor's heal /
//    pending / sharp-wait polls land the worker's answer there), else the
//    prop. The editor freezes the logo into its own state when it opens
//    and the canvas sync (patchLogoFromDetail) never copies productPending,
//    so the prop keeps saying "pending" long after the worker has landed
//    (under the deferred product every mutation answer is stamped pending)
//    - judged on it the button would stay locked until the 10-minute stale
//    line. The popup-fast list stub is not a detail and is ignored.
//  - a click writes ONLY the list rows (cacheWrite "rows"), never the
//    detail entry: see PriceRefreshNowCacheWrite in useLogoPriceRefreshNow.
// 0 calls: the entry is read through a subscription to the query cache
// (useSyncExternalStore), never fetched - a disabled useQuery would leave an
// idle entry behind that the editor screen's own mount reads.
// No detailStatus: the editor never mounts on a stub (the screen treats
// one as "still loading"), so there is nothing to wait for.
// Known limit of judging the lock on that entry while a click never marks
// it: without an editor mutation (no heal / pending poll re-reads it) the
// entry keeps saying "not pending", so the button opens again after the
// 60 s cooldown even when the worker still owes the answer; a deliberate
// second click then costs ONE extra product-refresh call (202 - the API's
// stamp is older than its cooldown - which evicts the caches and queues the
// lookup once more). The popup does not have this (its click marks the
// detail entry, which locks until a landing / the 10-minute stale line).
// The opposite case exists too: after a mutation the heal's three polls may
// all run before a slow worker lands, leaving the entry pending with a
// fresh stamp - the button then stays locked until the stale line although
// the API would accept a click after 60 s (the list / F5 unlocks it).
function EditorPriceRefreshNowButton(props: Props) {
  const pendingLogo = useFreshestCachedLogo(props.logo);
  return (
    <PriceRefreshNowButtonInner
      {...props}
      pendingLogo={pendingLogo}
      cacheWrite="rows"
    />
  );
}

// The logo's cached detail entry (["logos", actAs, id]) when it holds a real
// GetLogo for this logo, else the prop. The cache subscription is filtered
// BEFORE the snapshot: only an event on a detail entry of THIS logo
// (isLogoDetailQueryKey with the logo's id, any act-as scope - the scope
// segment is read live by logoDetailKey) reads the entry; every other cache
// event - the design editor's many queries, its mutations, the lists -
// costs one key check and no cache scan (getQueryData is a linear find in
// react-query v4). Re-renders only when the entry's reference changes (a
// snapshot of the same reference makes useSyncExternalStore bail out).
function useFreshestCachedLogo(logo: BaseLogo): BaseLogo {
  const queryClient = useQueryClient();
  const logoId = logo.id;
  const subscribe = useCallback(
    (onChange: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        const key = event.query.queryKey;
        if (isLogoDetailQueryKey(key) && key[2] === logoId) onChange();
      }),
    [queryClient, logoId]
  );
  const getSnapshot = useCallback(
    () => queryClient.getQueryData<BaseLogo>(logoDetailKey(logoId)),
    [queryClient, logoId]
  );
  const cached = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return cached && cached.id === logoId && !isLogoDetailFromList(cached)
    ? cached
    : logo;
}

function PriceRefreshNowButtonInner({
  logo,
  detailStatus,
  className,
  surface,
  pendingLogo,
  cacheWrite,
}: InnerProps) {
  const { t } = useTranslation();
  const { role } = useProfileContext();
  // Act-as: the profile (and its role) is the impersonated vendor's, while
  // the token the API judges (WithOrganizationAccess -> user.IsAdmin; the
  // route reads no actAsOrgId) is still the admin's. Impersonation state
  // can only be held by an admin (/profile/{orgId} is admin-only and the
  // profile provider drops a failed act-as), so an active act-as counts as
  // admin here - the same rule LoggedInMenu applies to its act-as entries.
  const { organizationId: impersonatedOrgId } =
    useOrganizationImpersonationContext();
  const { state, lockKind, retryInSeconds, refresh } = useLogoPriceRefreshNow(
    logo,
    cacheWrite ? { cacheWrite } : undefined
  );
  if (role !== Role.Admin && impersonatedOrgId == null) return null;
  if (logo.logoType !== LogoType.ImageLogo) return null;

  // The popup judges the prop (it IS the detail); the editor the freshest
  // cached detail it was handed (see EditorPriceRefreshNowButton).
  const judged = pendingLogo ?? logo;
  const pending =
    isLogoProductPending(judged) &&
    !isProductPendingStale(judged.productPendingSinceUtc);
  const loading = state === "loading";
  // Undefined = the caller has no background GetLogo (the editor's
  // toolbar): nothing to wait for.
  const detailNotReady = detailStatus !== undefined && detailStatus !== "ready";
  const disabled = state !== "idle" || pending || detailNotReady;
  const title = pending
    ? t("compLogoDesign.priceRefreshAlreadyPending")
    : state === "cooldown"
      ? lockKind === "dailyCap"
        ? t("compLogoDesign.priceRefreshDailyCap")
        : t("compLogoDesign.priceRefreshTryAgain", { n: retryInSeconds })
      : t("compLogoDesign.refreshPricesNow");

  // A row click on the logo list opens the detail: the click must stop here.
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    refresh();
  };

  // The tooltip sits on the wrapper: a disabled button swallows hover in
  // some browsers, so the button lets pointer events through while disabled.
  return (
    <span
      title={title}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center ${
        disabled ? "cursor-not-allowed" : ""
      } ${className ?? ""}`}
    >
      <button
        type="button"
        aria-label={title}
        aria-busy={loading}
        data-state={state}
        data-surface={surface === "editor" ? "editor" : undefined}
        data-testid="price-refresh-now-button"
        disabled={disabled}
        onClick={handleClick}
        className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none"
      >
        <FontAwesomeIcon icon={faArrowsRotate} className="w-3 h-3" spin={loading} />
      </button>
    </span>
  );
}
